import { useTranslation } from 'react-i18next';
import { MouseEvent } from 'react';
import * as amplitude from '@amplitude/analytics-browser';

import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { EMPTY_VALUE } from '@app/dashboard/constants/shared';
import { MADA_NAME, MADA_SCHEME } from '@app/dashboard/constants/transactions'
import { TRANSACTIONS_EVENTS, TRANSACTIONS_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/transactions';
import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';
import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';
import CopyText from '@app/dashboard/components/inner_layout/CopyText';

const FINGERPRINT_BTN_ID = 'fingerprint-copy-btn';

const resolvePaymentMethodDisplay = (paymentMethodList: { type: string; url: string }[]) =>
  paymentMethodList.reduce((result: any, item: any) => {
    if (item.type === 'img') result.leading_icon = item.url;
    else result.value = item.url;
    return result;
  }, {});

const PaymentMethods = ({
  id,
  singleTransactionDetails,
}: {
  id: string;
  singleTransactionDetails: Record<string, any>;
}) => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();

  const {
    payment_method,
    source_data,
    data: TrnxDataObj,
    public_customer_fingerprint: fingerprint,
  } = singleTransactionDetails || {};

  const copyFingerPrintHandler = (fingerprint: string) => {
    amplitude.track(TRANSACTIONS_EVENTS.FINGERPRINT_CLICKED, {
      [TRANSACTIONS_EVENTS_PARAMS.FINGERPRINT]: fingerprint,
      [TRANSACTIONS_EVENTS_PARAMS.TRNX_ID]: id,
    });
  };

  const fingerprintDeadClicksHandler = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    const target = e.target as HTMLElement;
    if (target.id !== FINGERPRINT_BTN_ID && target.parentElement?.id !== FINGERPRINT_BTN_ID) {
      amplitude.track(TRANSACTIONS_EVENTS.FINGERPRINT_DEAD_CLICKS, {
        [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
      });
    }
  };

  const getSchemeValue = () => {
    const acquirerId = TrnxDataObj?.migs_transaction?.acquirer?.id;

    if (acquirerId?.slice(0, 4).toUpperCase() === MADA_NAME) {
      return MADA_SCHEME;
    }

    return source_data?.sub_type || TrnxDataObj?.source?.scheme || EMPTY_VALUE;
  };

  const pairsList = [
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.Payment method'),
      ...resolvePaymentMethodDisplay(payment_method.payment_method),
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.type'),
      value: source_data?.card_info?.card_type || TrnxDataObj?.source?.card_type || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.scheme'),
      value: getSchemeValue(),
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.tier'),
      value: source_data?.card_info?.card_level || TrnxDataObj?.card_level || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.issuerBank'),
      value: TrnxDataObj?.source?.issuer || payment_method.issuer_bank || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.origin'),
      value: source_data?.card_info?.card_issuing_country || TrnxDataObj?.source?.issuer_country || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.bin'),
      value: source_data?.card_info?.card_first_6 || TrnxDataObj?.source?.bin || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.referenceNumber'),
      value: payment_method.reference_number || EMPTY_VALUE,
    },
    {
      label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.fingerprint'),
      render: () => (
        <CopyText
          textToCopy={fingerprint || EMPTY_VALUE}
          showTextToCopy
          onCopyTextHandler={copyFingerPrintHandler}
          buttonProps={{ id: FINGERPRINT_BTN_ID }}
        />
      ),
    },
  ];

  return (
    <InfoCard
      title={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.title')}
      noOfColumns={2}
      pairsList={pairsList}
      onClickHandler={fingerprintDeadClicksHandler}
    />
  );
};

export default PaymentMethods;
